import React from 'react';
import { Button, Space, SpaceProps, Popconfirm, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined, DeleteTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ExtraAction {
  icon: React.ReactNode;
  tooltip: string;
  onClick: (record: any) => void;
  disabled?: boolean;
  loading?: boolean;
}

interface ActionButtonsProps {
  record: any;
  baseUrl: string;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  onDelete?: (record: any) => void;
  size?: SpaceProps['size'];
  deleteConfirmTitle?: string;
  deleteConfirmDescription?: string;
  isLoading?: boolean;
  isDeleting?: boolean;
  extraActions?: ExtraAction[];
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  record,
  baseUrl,
  showView = true,
  showEdit = true,
  showDelete = true,
  onDelete,
  size = 'small',
  deleteConfirmTitle = 'common.deleteRecord',
  deleteConfirmDescription = 'common.deleteRecordConfirmation',
  isLoading = false,
  isDeleting = false,
  extraActions = [],
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Space size={size}>
      {showView && (
        <Tooltip title={t('common.view')}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`${baseUrl}/${record.id}`)}
            disabled={isDeleting}
          />
        </Tooltip>
      )}

      {showEdit && (
        <Tooltip title={t('common.update')}>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`${baseUrl}/${record.id}/update`)}
            disabled={isDeleting}
          />
        </Tooltip>
      )}

      {extraActions.map((action, index) => (
        <Tooltip key={index} title={action.tooltip}>
          <Button
            type="text"
            icon={action.icon}
            size="small"
            onClick={() => action.onClick(record)}
            disabled={action.disabled || isDeleting}
            loading={action.loading}
          />
        </Tooltip>
      ))}

      {showDelete && (
        <Popconfirm
          title={t(deleteConfirmTitle)}
          description={t(deleteConfirmDescription)}
          onConfirm={() => onDelete && onDelete(record)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
          disabled={isDeleting}
        >
          <Tooltip title={t('common.delete')}>
            <Button
              type="text"
              icon={<DeleteTwoTone twoToneColor="#d4283c" />}
              size="small"
              loading={isLoading || isDeleting}
              disabled={isDeleting}
            />
          </Tooltip>
        </Popconfirm>
      )}
    </Space>
  );
};

export default ActionButtons;
